import React, { useState, useEffect, useCallback, useRef } from 'react';
import { displayBoardApi, branchesApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { ArrowClockwise, ArrowsOut, ArrowsIn, Phone, Info, Eye, Gear, ChartLineUp, Monitor, ShieldWarning } from '@phosphor-icons/react';

const STATUS_CONFIG = {
  registered:          { dot: '#6366F1', label: 'Registered',        emoji: '' },
  eligibility_pending: { dot: '#F59E0B', label: 'Eligibility Check', emoji: '' },
  eligibility_verified:{ dot: '#10B981', label: 'Verified',          emoji: '' },
  preauth_pending:     { dot: '#F59E0B', label: 'Pre-Auth Pending',  emoji: '' },
  preauth_approved:    { dot: '#10B981', label: 'Pre-Auth OK',       emoji: '' },
  preauth_rejected:    { dot: '#EF4444', label: 'Pre-Auth Rejected', emoji: '' },
  documents_pending:   { dot: '#F97316', label: 'Pending Docs',      emoji: '' },
  documents_complete:  { dot: '#10B981', label: 'Docs Complete',     emoji: '' },
  bill_preparation:    { dot: '#3B82F6', label: 'Billing',           emoji: '' },
  claim_ready:         { dot: '#0EA5E9', label: 'Claim Ready',       emoji: '' },
  submitted:           { dot: '#3B82F6', label: 'Submitted',         emoji: '' },
  query_raised:        { dot: '#F97316', label: 'Query Raised',      emoji: '' },
  denied:              { dot: '#EF4444', label: 'Denied',            emoji: '' },
  under_appeal:        { dot: '#8B5CF6', label: 'Under Appeal',      emoji: '' },
  payment_pending:     { dot: '#F59E0B', label: 'Payment Pending',   emoji: '' },
  partially_settled:   { dot: '#059669', label: 'Partial Settlement', emoji: '' },
  settled:             { dot: '#059669', label: 'Settled',            emoji: '' },
};

const getStatusInfo = (s) => STATUS_CONFIG[s] || { dot: '#94A3B8', label: s?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Unknown', emoji: '' };

const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const MODES = [
  { value: 'patient', label: 'Patient View', icon: Eye },
  { value: 'operations', label: 'Operations', icon: Gear },
  { value: 'management', label: 'Management', icon: ChartLineUp },
];

const DisplayBoardPage = () => {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState(null);
  const [branches, setBranches] = useState([]);
  const [boardInfo, setBoardInfo] = useState({ hospital_name: '', branch_name: '', helpdesk_number: '', logo_url: '', is_platform_view: false });
  const [branchFilter, setBranchFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [mode, setMode] = useState('patient');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const tableBodyRef = useRef(null);
  const scrollRef = useRef(null);
  const isSuperAdmin = user?.role === 'super_admin';

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Auto-scroll
  useEffect(() => {
    const el = tableBodyRef.current;
    if (!el || claims.length <= 8) return;
    let scrollPos = 0;
    const rowHeight = 56;
    const totalHeight = claims.length * rowHeight;
    const visibleHeight = el.clientHeight;
    let direction = 1;
    const scroll = () => {
      scrollPos += direction * 1;
      if (scrollPos >= totalHeight - visibleHeight) { direction = -1; }
      if (scrollPos <= 0) { direction = 1; scrollPos = 0; }
      el.scrollTop = scrollPos;
    };
    scrollRef.current = setInterval(scroll, 50);
    const pause = () => { if (scrollRef.current) clearInterval(scrollRef.current); };
    const resume = () => { scrollRef.current = setInterval(scroll, 50); };
    el.addEventListener('mouseenter', pause);
    el.addEventListener('mouseleave', resume);
    return () => {
      if (scrollRef.current) clearInterval(scrollRef.current);
      el.removeEventListener('mouseenter', pause);
      el.removeEventListener('mouseleave', resume);
    };
  }, [claims]);

  const loadData = useCallback(async () => {
    try {
      const params = { mode };
      if (branchFilter !== 'all') params.branch_id = branchFilter;
      if (departmentFilter !== 'all') params.department = departmentFilter;
      const infoParams = { branch_id: branchFilter };
      const [res, brRes, infoRes] = await Promise.all([
        displayBoardApi.get(params),
        branchesApi.list(),
        displayBoardApi.info(infoParams),
      ]);
      if (mode === 'management') {
        setClaims(res.data.claims || []);
        setSummary(res.data.summary || null);
      } else {
        setClaims(res.data);
        setSummary(null);
      }
      setBranches(brRes.data);
      setBoardInfo(infoRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [mode, branchFilter, departmentFilter]);

  useEffect(() => { loadData(); const iv = setInterval(loadData, 30000); return () => clearInterval(iv); }, [loadData]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) { document.documentElement.requestFullscreen(); setIsFullscreen(true); }
    else { document.exitFullscreen(); setIsFullscreen(false); }
  };

  const departments = [...new Set(claims.map(c => c.department).filter(Boolean))];
  const dateStr = currentTime.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  const timeStr = currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
  const hospitalName = boardInfo.hospital_name || 'Hospital';
  const branchName = boardInfo.branch_name || '';
  const helpdesk = boardInfo.helpdesk_number || '';
  const fs = isFullscreen;
  const displayLogo = boardInfo.logo_url || '';

  // Super Admin cannot access Display Board - patient data is restricted
  if (isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center" data-testid="display-board-access-denied">
        <ShieldWarning className="h-16 w-16 text-slate-300 mb-4" weight="duotone" />
        <h2 className="text-xl font-semibold text-slate-700 font-['Outfit']">Access Restricted</h2>
        <p className="text-sm text-slate-500 mt-2 max-w-md">
          Display Board contains patient data and is accessible only to hospital users.
          Super Admin does not have access to hospital operational data.
        </p>
      </div>
    );
  }

  return (
    <div className={`${fs ? 'fixed inset-0 z-[9999] bg-slate-950 p-0 flex flex-col' : 'space-y-4'}`} data-testid="display-board-page">
      {/* Controls bar (non-fullscreen) */}
      {!fs && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Monitor className="h-6 w-6 text-sky-600" weight="duotone" />
            <div>
              <h1 className="text-xl font-semibold text-slate-800 font-['Outfit']">Display Board</h1>
              <p className="text-xs text-slate-500">TV / Waiting area display for patients & staff</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex bg-slate-100 rounded-lg p-0.5 border border-slate-200">
              {MODES.map(m => (
                <button key={m.value} onClick={() => setMode(m.value)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 ${mode === m.value ? 'bg-white shadow-sm text-sky-700' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid={`mode-${m.value}`}>
                  <m.icon className="h-3.5 w-3.5" />{m.label}
                </button>
              ))}
            </div>
            <Select value={branchFilter} onValueChange={setBranchFilter}>
              <SelectTrigger className="w-36 text-xs border-slate-200" data-testid="branch-filter">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-36 text-xs border-slate-200" data-testid="dept-filter">
                <SelectValue placeholder="All Depts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={loadData} className="h-9 w-9 border-slate-200" data-testid="refresh-btn">
              <ArrowClockwise className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" onClick={toggleFullscreen} className="h-9 w-9 border-slate-200" data-testid="fullscreen-btn">
              <ArrowsOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ========== TV DISPLAY ========== */}
      <div className={`${fs ? 'flex-1 flex flex-col' : 'rounded-xl overflow-hidden border border-slate-200 shadow-lg'}`} data-testid="tv-display">
        {/* TV Header */}
        <div className={`${fs ? 'bg-gradient-to-r from-slate-900 via-sky-950 to-slate-900' : 'bg-gradient-to-r from-slate-800 via-sky-900 to-slate-800'} px-6 py-3 flex items-center justify-between`} data-testid="tv-header">
          <div className="flex items-center gap-4">
            {displayLogo ? (
              <img src={displayLogo} alt="Logo" className="h-10 object-contain" data-testid="hospital-logo" />
            ) : (
              <div className="h-10 w-10 rounded-lg bg-white/15 backdrop-blur-sm flex items-center justify-center border border-white/20" data-testid="hospital-logo">
                <span className="text-xl font-bold text-white font-['Outfit']">{hospitalName.charAt(0)}</span>
              </div>
            )}
            <div>
              <h2 className={`font-bold text-white font-['Outfit'] tracking-wide ${fs ? 'text-2xl' : 'text-lg'}`} data-testid="hospital-name">
                {hospitalName}{branchName ? ` - ${branchName}` : ''}
              </h2>
              <p className="text-sky-300 text-xs tracking-wider uppercase font-medium">Claim Status Display</p>
            </div>
          </div>
          <div className="text-right flex items-center gap-4">
            {fs && (
              <button onClick={toggleFullscreen} className="text-slate-400 hover:text-white transition-colors" data-testid="exit-fullscreen-btn">
                <ArrowsIn className="h-5 w-5" />
              </button>
            )}
            <div>
              <p className={`text-white font-mono font-bold ${fs ? 'text-xl' : 'text-base'}`} data-testid="live-time">{timeStr}</p>
              <p className="text-sky-300 text-xs" data-testid="live-date">{dateStr}</p>
            </div>
          </div>
        </div>

        {/* Management Summary Row */}
        {mode === 'management' && summary && (
          <div className={`grid grid-cols-5 gap-0 ${fs ? 'bg-slate-900' : 'bg-slate-50'}`} data-testid="management-summary">
            {[
              { label: 'Total Claims', value: summary.total_claims, color: 'text-sky-400' },
              { label: 'Total Billed', value: formatCurrency(summary.total_billed), color: 'text-violet-400' },
              { label: 'Total Paid', value: formatCurrency(summary.total_paid), color: 'text-emerald-400' },
              { label: 'Outstanding', value: formatCurrency(summary.total_outstanding), color: 'text-rose-400' },
              { label: 'Urgent', value: summary.urgent_claims, color: 'text-amber-400' },
            ].map((s, i) => (
              <div key={i} className={`py-3 px-4 text-center border-r last:border-r-0 ${fs ? 'border-slate-800' : 'border-slate-200'}`}>
                <p className={`text-[10px] uppercase tracking-wider font-semibold ${fs ? 'text-slate-500' : 'text-slate-400'}`}>{s.label}</p>
                <p className={`text-lg font-bold font-['Outfit'] ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className={`flex items-center justify-center py-32 ${fs ? 'bg-slate-950' : 'bg-white'}`}>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-500"></div>
          </div>
        ) : claims.length === 0 ? (
          <div className={`flex flex-col items-center justify-center py-32 ${fs ? 'bg-slate-950 text-slate-500' : 'bg-white text-slate-400'}`}>
            <Monitor className="h-16 w-16 mb-3" weight="light" />
            <p className={`text-lg font-['Outfit'] ${fs ? 'text-slate-400' : 'text-slate-600'}`}>No Active Claims</p>
            <p className="text-sm mt-1">Claims will appear here as they are registered</p>
          </div>
        ) : (
          <div className={`flex-1 flex flex-col ${fs ? 'bg-slate-950' : 'bg-white'}`}>
            {/* Table Header */}
            <div className={`grid ${mode === 'management' ? 'grid-cols-[80px_1fr_120px_1fr_120px_120px_140px]' : mode === 'operations' ? 'grid-cols-[80px_1fr_120px_1fr_160px_120px_120px]' : 'grid-cols-[70px_minmax(140px,1.2fr)_100px_80px_85px_110px_minmax(120px,1fr)_130px_90px]'} gap-x-3 px-6 py-3 ${fs ? 'bg-slate-900/80 border-b border-slate-800' : 'bg-slate-50 border-b border-slate-200'}`} data-testid="table-header">
              <div className={`text-xs font-bold uppercase tracking-wider ${fs ? 'text-sky-400' : 'text-sky-700'}`}>Token</div>
              <div className={`text-xs font-bold uppercase tracking-wider ${fs ? 'text-slate-400' : 'text-slate-500'}`}>Patient</div>
              {mode === 'patient' && <div className={`text-xs font-bold uppercase tracking-wider ${fs ? 'text-slate-400' : 'text-slate-500'}`}>UHID</div>}
              {mode === 'patient' && <div className={`text-xs font-bold uppercase tracking-wider ${fs ? 'text-slate-400' : 'text-slate-500'}`}>IP No</div>}
              {mode === 'patient' && <div className={`text-xs font-bold uppercase tracking-wider ${fs ? 'text-slate-400' : 'text-slate-500'}`}>Dept</div>}
              {mode === 'patient' && <div className={`text-xs font-bold uppercase tracking-wider ${fs ? 'text-amber-400' : 'text-amber-600'}`}>Claim No</div>}
              <div className={`text-xs font-bold uppercase tracking-wider ${fs ? 'text-slate-400' : 'text-slate-500'}`}>TPA / Insurance</div>
              <div className={`text-xs font-bold uppercase tracking-wider ${fs ? 'text-slate-400' : 'text-slate-500'}`}>Status</div>
              {mode === 'operations' && <div className={`text-xs font-bold uppercase tracking-wider ${fs ? 'text-slate-400' : 'text-slate-500'}`}>Docs</div>}
              {mode === 'management' && <div className={`text-xs font-bold uppercase tracking-wider text-right ${fs ? 'text-slate-400' : 'text-slate-500'}`}>Billed</div>}
              <div className={`text-xs font-bold uppercase tracking-wider ${fs ? 'text-slate-400' : 'text-slate-500'}`}>{mode === 'management' ? 'Paid' : 'Action'}</div>
            </div>

            {/* Table Body - Scrollable */}
            <div ref={tableBodyRef} className={`flex-1 overflow-hidden ${fs ? 'max-h-[calc(100vh-220px)]' : 'max-h-[500px]'}`} data-testid="table-body">
              {claims.map((claim, idx) => {
                const si = getStatusInfo(claim.status);
                const isEven = idx % 2 === 0;
                return (
                  <div key={claim.id}
                    className={`grid ${mode === 'management' ? 'grid-cols-[80px_1fr_120px_1fr_120px_120px_140px]' : mode === 'operations' ? 'grid-cols-[80px_1fr_120px_1fr_160px_120px_120px]' : 'grid-cols-[70px_minmax(140px,1.2fr)_100px_80px_85px_110px_minmax(120px,1fr)_130px_90px]'} gap-x-3 px-6 py-3 items-center transition-colors border-b ${
                      fs
                        ? `${isEven ? 'bg-slate-950' : 'bg-slate-900/40'} border-slate-800/60 hover:bg-sky-950/30`
                        : `${isEven ? 'bg-white' : 'bg-slate-50/60'} border-slate-100 hover:bg-sky-50/50`
                    }`}
                    data-testid={`claim-row-${claim.id}`}
                  >
                    {/* Token */}
                    <div>
                      <span className={`inline-flex items-center justify-center h-9 w-14 rounded-lg font-bold font-['Outfit'] text-base ${
                        fs ? 'bg-sky-500/20 text-sky-300 border border-sky-500/30' : 'bg-sky-100 text-sky-700 border border-sky-200'
                      }`} data-testid={`token-${claim.token}`}>
                        {claim.token}
                      </span>
                    </div>

                    {/* Patient Name */}
                    <div>
                      <p className={`font-semibold text-sm ${fs ? 'text-white' : 'text-slate-800'}`} data-testid={`patient-${claim.id}`}>
                        {claim.patient_name}
                      </p>
                    </div>

                    {/* UHID */}
                    {mode === 'patient' && (
                      <div>
                        <span className={`text-xs font-mono font-medium ${fs ? 'text-sky-300' : 'text-sky-700'}`} data-testid={`uhid-${claim.id}`}>
                          {claim.uhid || '-'}
                        </span>
                      </div>
                    )}

                    {/* IP No */}
                    {mode === 'patient' && (
                      <div>
                        <span className={`text-xs font-mono font-medium ${fs ? 'text-violet-300' : 'text-violet-700'}`} data-testid={`ipd-${claim.id}`}>
                          {claim.ipd_number || '-'}
                        </span>
                      </div>
                    )}

                    {/* Department */}
                    {mode === 'patient' && (
                      <div>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          fs ? 'bg-slate-800 text-slate-300 border border-slate-700' : 'bg-slate-100 text-slate-600 border border-slate-200'
                        }`}>
                          {claim.department || 'General'}
                        </span>
                      </div>
                    )}

                    {/* Insurance Claim No */}
                    {mode === 'patient' && (
                      <div>
                        <span className={`text-xs font-mono font-medium ${claim.insurance_claim_no ? (fs ? 'text-amber-300' : 'text-amber-600') : (fs ? 'text-slate-700' : 'text-slate-300')}`}
                          data-testid={`ins-claim-${claim.id}`}>
                          {claim.insurance_claim_no || '-'}
                        </span>
                      </div>
                    )}

                    {/* TPA / Insurance */}
                    <div className={`text-sm ${fs ? 'text-slate-300' : 'text-slate-600'}`}>
                      <span>{claim.payer_name}</span>
                      {claim.claim_type && (
                        <span className={`ml-2 text-[10px] uppercase px-1.5 py-0.5 rounded ${
                          fs ? 'bg-slate-800 text-slate-500' : 'bg-slate-100 text-slate-400'
                        }`}>{claim.claim_type}</span>
                      )}
                    </div>

                    {/* Status */}
                    <div className="flex items-center gap-2">
                      <span className="h-3 w-3 rounded-full flex-shrink-0 shadow-sm" style={{ backgroundColor: si.dot, boxShadow: `0 0 8px ${si.dot}40` }}></span>
                      <span className={`text-sm font-medium ${fs ? 'text-slate-200' : 'text-slate-700'}`}>{si.label}</span>
                    </div>

                    {/* Operations: Docs count */}
                    {mode === 'operations' && (
                      <div className={`text-sm ${fs ? 'text-slate-400' : 'text-slate-500'}`}>
                        {claim.documents_count !== undefined ? `${claim.documents_count} docs` : '-'}
                        {claim.days_old > 0 && <span className={`ml-1 text-[10px] ${claim.days_old > 30 ? 'text-red-400' : ''}`}>({claim.days_old}d)</span>}
                      </div>
                    )}

                    {/* Management: Billed */}
                    {mode === 'management' && (
                      <div className={`text-sm text-right font-medium ${fs ? 'text-sky-400' : 'text-sky-700'}`}>
                        {formatCurrency(claim.billed_amount)}
                      </div>
                    )}

                    {/* Action / Paid */}
                    <div>
                      {mode === 'management' ? (
                        <span className={`text-sm font-medium ${fs ? 'text-emerald-400' : 'text-emerald-700'}`}>
                          {formatCurrency(claim.paid_amount)}
                        </span>
                      ) : (
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          fs ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {claim.pending_stage || (claim.status === 'settled' ? 'Complete' : claim.status === 'denied' ? 'Appeal' : 'In Progress')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Auto-scroll indicator */}
            {claims.length > 8 && (
              <div className={`text-center py-2 text-xs animate-pulse ${fs ? 'text-slate-600 bg-slate-900/50' : 'text-slate-400 bg-slate-50'}`}>
                Auto-scrolling for more patients
              </div>
            )}
          </div>
        )}

        {/* TV Footer */}
        <div className={`${fs ? 'bg-gradient-to-r from-slate-900 via-sky-950 to-slate-900 border-t border-slate-800' : 'bg-gradient-to-r from-slate-800 via-sky-900 to-slate-800'} px-6 py-3`} data-testid="tv-footer">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Info className="h-4 w-4 text-sky-300" />
                <span className={`text-sky-100 text-sm ${fs ? 'text-base' : ''}`}>Please contact Billing Desk for assistance</span>
              </div>
              {helpdesk && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-emerald-400" />
                  <span className={`text-emerald-300 font-semibold text-sm ${fs ? 'text-base' : ''}`}>Helpdesk: {helpdesk}</span>
                </div>
              )}
            </div>
            <div className="text-xs text-slate-500">
              Care'O Powered by Steward Healthcare India Pvt Ltd
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisplayBoardPage;
